/**
 * Helpers for the user-editable lead `createdAt` (Settings → Manage →
 * "Add/Edit Created At"). The form keeps a FULL ISO timestamp; the pickers
 * work in local calendar days ("YYYY-MM-DD"). Composing a new timestamp keeps
 * a time-of-day (the lead's original one on edit, "now" on create) so an
 * untouched field round-trips byte-identically and a re-dated lead doesn't
 * collapse to midnight — and clamps to now, since a created time can never
 * be in the future (the server rejects it).
 */

const pad = (n: number) => String(n).padStart(2, "0");

/** Local calendar day of an ISO timestamp, or "" when absent/invalid. */
export function toLocalDayIso(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function todayLocalDayIso(): string {
  return toLocalDayIso(new Date().toISOString());
}

/**
 * Build the ISO timestamp for `dayIso` ("YYYY-MM-DD", local). Time-of-day
 * comes from `baseIso` when given (edit: keep the lead's original time),
 * else from now (create). Clamped to now.
 */
export function composeCreatedAt(dayIso: string, baseIso?: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayIso);
  const now = new Date();
  if (!m) return now.toISOString();
  const base = baseIso ? new Date(baseIso) : now;
  const time = Number.isNaN(base.getTime()) ? now : base;
  const next = new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    time.getHours(),
    time.getMinutes(),
    time.getSeconds(),
    time.getMilliseconds(),
  );
  if (next.getTime() > now.getTime()) return now.toISOString();
  return next.toISOString();
}

/** "07 Sep 2026" — the read-only rendering in the lead view. */
export function formatCreatedAtDay(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
